import {Box, Typography, Link, useTheme} from '@mui/material';
import Loader from '@jobseeker/lotus/components/Loader';
import {Button} from '@jobseeker/lotus/elements';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {deferTask} from '@jobseeker/common-core/utils';
import {CookieBannerProps} from './types';

const CookieBannerFeature = ({
  showCookieBanner,
  saveCookiePolicyAcceptance,
  loading,
  setLoading,
  CTA,
  policyLinks,
}: CookieBannerProps) => {
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
  const theme = useTheme();
  const isLargeDevice = useIsLargeDevice();

  const handleCookiePolicyAccepted = () => {
    deferTask(() => {
      setLoading(true);
      saveCookiePolicyAcceptance();
    });
  };

  return (
    showCookieBanner && (
      <Box
        data-testid="cookie_policy_banner"
        display={'flex'}
        gap={isLargeDevice ? 1.5 : 2}
        justifyContent={'center'}
        alignItems={isLargeDevice ? 'center' : 'flex-start'}
        flexDirection={isLargeDevice ? 'row' : 'column'}
        py={isLargeDevice ? 1.75 : 3}
        paddingLeft={isLargeDevice ? 0 : 1.875}
        paddingRight={isLargeDevice ? 0 : 3.5}
        position={'sticky'}
        zIndex={99}
        bottom={!isAppIimjobs && isLargeDevice ? 52 : 0}
        sx={{
          background: isAppIimjobs
            ? 'linear-gradient(90deg, rgba(34, 84, 72, 0.8) 0%, rgba(126, 159, 103, 0.8) 100%)'
            : '#13355c',
          opacity: isAppIimjobs ? 1 : 0.9,
        }}
      >
        <Typography
          component={'span'}
          variant="subtitle2"
          fontWeight={600}
          color={'primary.contrastText'}
          lineHeight={'20px'}
          fontSize={isAppIimjobs ? '14px' : '16px'}
        >
          We use cookies to improve your experience. By continuing to browse the
          site, you agree to our
          <Link
            href={policyLinks.privacyPolicy}
            target="_blank"
            variant="subtitle2"
            lineHeight={'20px'}
            color={isAppIimjobs ? 'prompt.main' : '#fff'}
            mx={0.625}
            sx={{
              textDecoration: 'underline',
            }}
            fontWeight={isAppIimjobs ? 500 : 700}
            fontSize={isAppIimjobs ? 14 : 16}
          >
            Privacy Policy
          </Link>
          &
          <Link
            href={policyLinks.cookiePolicy}
            target="_blank"
            variant="subtitle2"
            lineHeight={'20px'}
            color={isAppIimjobs ? 'prompt.main' : '#fff'}
            mx={0.625}
            sx={{
              textDecoration: 'underline',
            }}
            fontWeight={isAppIimjobs ? 500 : 700}
            fontSize={isAppIimjobs ? 14 : 16}
          >
            Cookie Policy
          </Link>
        </Typography>
        <Button
          onClick={handleCookiePolicyAccepted}
          data-testid="cookie_policy_cta"
          sx={{
            backgroundColor: isAppIimjobs
              ? 'secondary.main'
              : theme.palette.primary.main,
            color: 'primary.contrastText',
            fontSize: theme.typography.pxToRem(16),
            lineHeight: '20px',
            fontWeight: isAppIimjobs ? 500 : 700,
            opacity: 1,
            px: 2,
            py: 1,
            borderRadius: isAppIimjobs ? 8 : 1.5,
            boxShadow: '0px 0.68px 0px 0px #AAAAAA1F',
            ':hover': {
              backgroundColor: 'secondary.main',
            },
          }}
        >
          {!loading ? (
            CTA
          ) : (
            <Loader size={20} sx={{color: 'primary.contrastText'}} />
          )}
        </Button>
      </Box>
    )
  );
};

export default CookieBannerFeature;
